import React, { useRef, useEffect, useState } from 'react';
import { Typography, DatePicker, Button, Tooltip } from 'antd';
import dayjs from '../../dayjs';
import './DateCarousel.css';
import PlotRadial from "./PlotRadial";
import { DATE_STRING } from '../../model/schedule';
import {ReactComponent as Down} from '../../assets/icons/Down.svg';
import { LeftOutlined, RightOutlined, InfoCircleOutlined } from '@ant-design/icons';
import {ReactComponent as DownArrow } from '../../assets/icons/DownArrow.svg';
const { Text } = Typography;

const DateCarousel = ({ selectedDate, onDateSelect, bookings }) => {
  const [dates, setDates] = useState([]);
  const containerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [draggedDistance, setDraggedDistance] = useState(0);
  const [open, setOpen] = useState(false);
  const [displayMonth, setDisplayMonth] = useState(selectedDate);

  // Update displayMonth when selectedDate changes
  useEffect(() => {
    setDisplayMonth(selectedDate);
  }, [selectedDate]);

  // Generate 30 days from today
  useEffect(() => {
    const datesArray = [];
    const today = dayjs();
    
    // Generate more dates to ensure we have enough for scrolling
    // Generate 30 days before and after the selected date
    const startDate = selectedDate.subtract(30, 'day');
    const endDate = selectedDate.add(30, 'day');
    
    let currentDate = startDate;
    while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, 'day')) {
      datesArray.push({
        date: currentDate,
        dayNumber: currentDate.date(),
        dayName: currentDate.format('ddd'),
        monthYear: currentDate.format('MMMM YYYY'),
        monthAbbr: currentDate.format('MMM'),
        isToday: currentDate.isSame(today, 'day'),
        isSelected: currentDate.isSame(selectedDate, 'day'),
        // Get bookings for this date
        bookings: bookings.filter(booking => {
          const bookingDates = booking.dates?.length ? booking.dates : [booking.startDate];
          return bookingDates.some(date => {
            const bookingDate = date;
            // If bookingDate is already a dayjs object, use it directly
            const dateToCompare = bookingDate?.isDayjs ? bookingDate : dayjs(bookingDate, DATE_STRING);
            return dateToCompare?.format('YYYY-MM-DD') === currentDate.format('YYYY-MM-DD');
          });
        })
      });
      currentDate = currentDate.add(1, 'day');
    }
    setDates(datesArray);
  }, [selectedDate, bookings]);

  // Center the selected date when it changes
  useEffect(() => {
    // Add a small delay to ensure the dates are rendered
    const timer = setTimeout(() => {
      if (containerRef.current) {
        const container = containerRef.current;
        const selectedElement = container.querySelector(`[data-date="${selectedDate.format('YYYY-MM-DD')}"]`);
        if (selectedElement) {
          // Calculate the position to scroll to (center of the selected element)
          const scrollTo = selectedElement.offsetLeft - (container.offsetWidth / 2) + (selectedElement.offsetWidth / 2);
          
          // Use a more controlled scroll animation to prevent bouncing
          const startPosition = container.scrollLeft;
          const distance = scrollTo - startPosition;
          const duration = 300; // ms
          const startTime = performance.now();
          
          const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
          
          const animateScroll = (currentTime) => {
            const elapsedTime = currentTime - startTime;
            const progress = Math.min(elapsedTime / duration, 1);
            
            container.scrollLeft = startPosition + (distance * easeOutCubic(progress));
            
            if (progress < 1) {
              requestAnimationFrame(animateScroll);
            }
          };
          
          requestAnimationFrame(animateScroll);
        }
      }
    }, 50); // Reduced delay for faster response
    
    return () => clearTimeout(timer);
  }, [selectedDate]);

  // Add an effect to run once when the component mounts
  useEffect(() => {
    // Show a hint about the drag functionality
    if (containerRef.current) {
      // Add a subtle animation to show the carousel can be dragged
      const container = containerRef.current;
      
      // Only animate if there are enough dates to scroll
      if (container.scrollWidth > container.clientWidth) {
        // Animate a small scroll to the right and back
        const initialScroll = container.scrollLeft;
        const scrollDistance = 40;
        
        // Use a timeout to make sure we don't execute this too early
        const timer = setTimeout(() => {
          // First scroll right a bit
          container.scrollTo({
            left: initialScroll + scrollDistance,
            behavior: 'smooth'
          });
          
          // Then scroll back
          setTimeout(() => {
            container.scrollTo({
              left: initialScroll,
              behavior: 'smooth'
            });
          }, 400);
        }, 800);
        
        return () => clearTimeout(timer);
      }
    }
  }, []);

  const getDateStatus = (dateBookings) => {
    if (!dateBookings.length) return 'empty';
    
    const statuses = dateBookings.map(b => b.status);
    if (statuses.includes('scheduled')) return 'scheduled';
    if (statuses.includes('accepted')) return 'accepted';
    return 'pending';
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - containerRef.current.offsetLeft);
    setScrollLeft(containerRef.current.scrollLeft);
    setDraggedDistance(0);
    // Change cursor to grabbing
    if (containerRef.current) {
      containerRef.current.style.cursor = 'grabbing';
      containerRef.current.classList.add('dragging');
    }
  };

  const handleMouseUp = (e) => {
    if (!isDragging) return;
    setIsDragging(false);
    
    // Reset cursor
    if (containerRef.current) {
      containerRef.current.style.cursor = 'grab';
      containerRef.current.classList.remove('dragging');
    }

    // Only select a date if we've dragged a small amount or haven't dragged at all
    if (Math.abs(draggedDistance) <= 5) {
      // This was essentially a click, not a drag - select the date directly
      if (e.target.closest('.date-card')) {
        const dateValue = e.target.closest('.date-card').getAttribute('data-date');
        if (dateValue) {
          onDateSelect(dayjs(dateValue));
        }
      }
    } else {
      // This was a drag - just snap to the closest date 
      // without changing the selected date
      if (containerRef.current) {
        const container = containerRef.current;
        const centerX = container.getBoundingClientRect().left + container.offsetWidth / 2;
        const dateElements = container.getElementsByClassName('date-card');
        let closestDate = null;
        let minDistance = Infinity;

        Array.from(dateElements).forEach((element) => {
          const rect = element.getBoundingClientRect();
          const distance = Math.abs(rect.left + rect.width / 2 - centerX);
          if (distance < minDistance) {
            minDistance = distance;
            closestDate = element.getAttribute('data-date');
          }
        });

        if (closestDate) {
          // Smooth scroll to center the closest date for visual alignment
          const selectedElement = container.querySelector(`[data-date="${closestDate}"]`);
          if (selectedElement) {
            const scrollTo = selectedElement.offsetLeft - (container.offsetWidth / 2) + (selectedElement.offsetWidth / 2);
            container.scrollTo({ left: scrollTo, behavior: 'smooth' });
          }
          
          // Don't auto-select the date after a long drag, let the user explicitly click to select
        }
      }
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    
    if (!containerRef.current) return;
    
    const x = e.pageX - containerRef.current.offsetLeft;
    const walk = (x - startX); // Use 1:1 movement ratio for natural scrolling
    setDraggedDistance(walk);
    containerRef.current.scrollLeft = scrollLeft - walk;
  };

  const animateScroll = (element, targetPosition, duration = 300) => {
    const startPosition = element.scrollLeft;
    const distance = targetPosition - startPosition;
    const startTime = performance.now();

    const easeOutQuad = (t) => t * (2 - t);

    const animation = (currentTime) => {
      const elapsedTime = currentTime - startTime;
      const progress = Math.min(elapsedTime / duration, 1);
      
      const currentScroll = startPosition + (distance * easeOutQuad(progress));
      element.scrollLeft = currentScroll;
      
      if (progress < 1) {
        requestAnimationFrame(animation);
      }
    };

    requestAnimationFrame(animation);
  };

  const handleDateClick = (date, e) => {
    // Skip this handler when we're dragging
    // The drag handler will take care of selecting dates when appropriate
    if (isDragging || Math.abs(draggedDistance) > 5) {
      return;
    }
    
    onDateSelect(date);
    
    // Update the display month when a date is selected
    setDisplayMonth(date);
    
    // Center the clicked date with smooth animation
    const container = containerRef.current;
    if (!container) return;
    
    const element = e.currentTarget;
    const scrollTo = element.offsetLeft - (container.offsetWidth / 2) + (element.offsetWidth / 2);
    
    // Cancel any ongoing animation
    if (container._animationFrame) {
      cancelAnimationFrame(container._animationFrame);
    }
    
    // Use custom animation
    animateScroll(container, scrollTo);
  };

  const handleDateChange = (date) => {
    if (!date) return;
    
    // Update the display month
    setDisplayMonth(date);
    
    // Select the middle day of the week (4th day)
    const middleDayOfWeek = date.startOf('week').add(3, 'day');
    onDateSelect(middleDayOfWeek);
    
    // Close the date picker
    setOpen(false);
  };

  // Add function to scroll to the previous set of dates
  const handlePrevScroll = () => {
    if (containerRef.current) {
      const container = containerRef.current;
      const cardWidth = 100; // Approximate width of each date card including margin
      const scrollDistance = cardWidth * 7; // Scroll by 7 days (one week)
      container.scrollBy({ left: -scrollDistance, behavior: 'smooth' });
      
      // Also update the selected date to go back one week
      const prevWeekDate = selectedDate.subtract(7, 'day');
      onDateSelect(prevWeekDate);
    }
  };

  // Add function to scroll to the next set of dates
  const handleNextScroll = () => {
    if (containerRef.current) {
      const container = containerRef.current;
      const cardWidth = 100; // Approximate width of each date card including margin
      const scrollDistance = cardWidth * 7; // Scroll by 7 days (one week)
      container.scrollBy({ left: scrollDistance, behavior: 'smooth' });
      
      // Also update the selected date to go forward one week
      const nextWeekDate = selectedDate.add(7, 'day');
      onDateSelect(nextWeekDate);
    }
  };

  return (
    <div className="date-carousel-container">
      <div className='date-carousel-header'>
        <div style={{ width: '30px' }}></div> {/* Empty space to balance the layout */}
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div className='plot-radial-date mb-0' style={{cursor: 'pointer'}} onClick={() => setOpen(true)}>
              {displayMonth.format('MMMM YYYY')}
              <Down style={{ marginLeft: '8px', width: 10, height: 10 }} />
              {open && (
                <DatePicker
                  picker="week"
                  mode="week"
                  format="wo-YYYY"
                  open={open}
                  onOpenChange={(visible) => setOpen(visible)}
                  onChange={handleDateChange}
                  onPanelChange={(value) => {
                    setDisplayMonth(value);
                  }}
                  value={selectedDate}
                />
              )}
          </div>
          <Button 
            type="text" 
            onClick={() => onDateSelect(dayjs())}
            style={{ 
              marginLeft: '8px', 
              color: '#6B54F9',
              fontWeight: 500,
              padding: '4px 12px',
              height: 'auto',
              outline: 'none'
            }}
          >
            Today
          </Button>
        </div>
        <Tooltip 
          title={
            <div>
              <p><span style={{ color: '#51B238', marginRight: '5px' }}>●</span> Low demand: More than 80% available</p>
              <p><span style={{ color: '#FAAD14', marginRight: '5px' }}>●</span> Medium demand: 20-80% available</p>
              <p><span style={{ color: '#FF4D4F', marginRight: '5px' }}>●</span> High demand: Less than 20% available</p>
            </div>
          } 
          placement="bottomRight"
        >
          <InfoCircleOutlined className="legend-info-icon" style={{ marginLeft: '8px', color: '#6B54F9', cursor: 'pointer' }} />
        </Tooltip>
      </div>
      <Button 
        className="carousel-arrow carousel-arrow-left" 
        icon={<DownArrow/>} 
        onClick={handlePrevScroll}
        type="text"
      />
      <div className="date-carousel-wrapper">
        <div 
          className="date-carousel"
          ref={containerRef}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseUp}
        >
          {dates.map(({ date, dayNumber, dayName, monthAbbr, isToday, isSelected, bookings: dateBookings }) => (
            <div
              key={date.format('YYYY-MM-DD')}
              data-date={date.format('YYYY-MM-DD')}
              className={`date-card ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''} status-${getDateStatus(dateBookings)}`}
              onClick={(e) => handleDateClick(date, e)}
            >
              <PlotRadial dateBookings={dateBookings} />
              <Text className="day-name">{dayName}</Text>
              <Text className="day-number">{dayNumber}</Text>
              <Text className="month-abbr">{monthAbbr}</Text>
              {dateBookings.length > 0 && (
                <Text className="booking-count">{dateBookings.length}</Text>
              )}
            </div>
          ))}
        </div>
      </div>
      <Button 
        className="carousel-arrow carousel-arrow-right" 
        icon={<DownArrow/>} 
        onClick={handleNextScroll}
        type="text"
      />
    </div>
  );
};

export default DateCarousel;